// Products (spec/dashboard.md section 9.10; spec/api-conventions.md section 19.1): the branch's products grouped by
// `referenceId` (`baseId`, else `itemId`) — the default row, then its platform override rows indented —, every cell an
// inline edit staged as a `product` draft, + Create, + Platform override…, Where used (`?product=<id>`), staged deletes,
// the "no USD rate" badge, the validator's refusals on the rows. Save All (the shell) sends POST / PATCH / DELETE per row.
import { useAssetUrls, useBranchAssetCtx } from '../../api/assetUrls';
import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Json, Parameter, Product } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button, IconButton } from '../../ui/Button';
import { Badge, draftTone } from '../../ui/Chip';
import { DataGrid, type Column } from '../../ui/DataGrid';
import { EmptyState } from '../../ui/Banner';
import { Field } from '../../ui/Field';
import { AlertCircle, AlertTriangle, CornerDownRight, ExternalLink, FileText, Layers, MoreVertical, Plus, Search, ShoppingBag } from '../../ui/icons';
import { Select, TextInput } from '../../ui/inputs';
import { Modal, useUrlParam } from '../../ui/Modal';
import { Menu } from '../../ui/Popover';
import { useQuery } from '../../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { entityKey, isTempId, type Draft } from '../../workspace/Workspace';
import { NUTAKU_SLOTS, PLATFORM_SLUG } from '../env/slots';
import { CURRENCY_PATTERN, exponentOf, formatMinor, parseMinor } from './money';
import './products.css';

const TYPES: { value: number; label: string }[] = [
  { value: 1, label: 'Consumable' },
  { value: 2, label: 'Non-consumable' },
  { value: 3, label: 'Subscription' },
];
const typeLabel = (t: number | string) => TYPES.find((x) => x.value === Number(t))?.label ?? `Type ${String(t)}`;

const NO_FX_TITLE = 'No USD rate for this currency: revenue is reported in the currency itself, never converted to USD. USD and Nutaku Gold (1 NG = 0.01 USD) are known.';
/** The currencies with a known USD rate (spec/api-conventions.md section 19.1): the badge is for every other one. */
const KNOWN_USD_RATES = new Set(['USD', 'NG']);

const isDefaultPlatform = (platform: string) => platform === 'default' || platform === 'all' || platform === '';
/** The key the clients overlay platform rows by: `baseId`, else `itemId` (section 19.1). */
const referenceOf = (p: Product) => (p.baseId && p.baseId.trim() !== '' ? p.baseId : p.itemId);

/** A synthetic parameter so the content widgets (asset picker, string picker) edit a product field. */
function synthetic(name: string, displayName: string, type: Parameter['type'], extra: Partial<Parameter> = {}): Parameter {
  return { id: '', unnyId: null, name, displayName, description: null, type, of: null, template: null, enum: null, group: null, assetKinds: [], regex: null, default: null, isUnique: false, isRequired: false, usedInName: false, flags: [], legacyFlags: 0, declaredBy: null, ...extra } as Parameter;
}
const ICON_PARAM = synthetic('iconUrl', 'Icon', 'asset', { assetKinds: ['image'] });
const LOC_NAME_PARAM = synthetic('localizedName', 'Localized name', 'localized_string');
const LOC_DESC_PARAM = synthetic('localizedDescription', 'Localized description', 'localized_string');

type ProductField = 'name' | 'itemId' | 'price' | 'type' | 'baseId' | 'description' | 'iconUrl' | 'localizedName' | 'localizedDescription' | 'platform';

interface Row {
  product: Product;
  draft: Draft | undefined;
  override: boolean;
  /** an override row whose reference names no default row on screen */
  orphan: boolean;
  /** a default row: the number of its platform override rows under it */
  overrides: number;
}

export function ProductsPage() {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const workspace = useWorkspace();
  const version = useWorkspaceVersion(workspace);
  const catalog = useContent();
  const canWrite = can('content_write') && !workspace.isProtected;
  const missing = can('content_write') ? null : 'content_write';
  const whereUsed = useUrlParam('product');
  const [search, setSearch] = useState('');
  const [focusSku, setFocusSku] = useState<string | null>(null);
  const [overrideFor, setOverrideFor] = useState<Product | null>(null);

  const serverById = useMemo(() => new Map(catalog.productList.map((p) => [p.id, p])), [catalog.productList]);

  // the rows: the server's products with their drafts overlaid (a deleted one stays, struck through), then the new drafts
  const rows = useMemo<Row[]>(() => {
    const all: { product: Product; draft: Draft | undefined }[] = [];
    for (const p of catalog.productList) {
      const draft = workspace.getDraft(entityKey('product', p.id));
      all.push({ product: draft && draft.status !== 'deleted' ? (draft.current as unknown as Product) : p, draft });
    }
    const fresh = workspace.all.filter((d) => d.kind === 'product' && d.status === 'new').sort((a, b) => a.createdAt - b.createdAt);
    for (const d of fresh) all.push({ product: d.current as unknown as Product, draft: d });

    const q = search.trim().toLowerCase();
    const matches = (p: Product) => !q || [p.itemId, p.baseId, p.name, p.legacyId].some((v) => typeof v === 'string' && v.toLowerCase().includes(q));
    const visible = all.filter((r) => matches(r.product));

    const bases = visible.filter((r) => isDefaultPlatform(r.product.platform));
    const overrides = visible.filter((r) => !isDefaultPlatform(r.product.platform));
    // new base rows first (creation order), then the saved ones by SKU — the API's order
    const newBases = bases.filter((r) => r.draft?.status === 'new');
    const savedBases = bases.filter((r) => r.draft?.status !== 'new').sort((a, b) => a.product.itemId.localeCompare(b.product.itemId));
    const out: Row[] = [];
    const placed = new Set<string>();
    for (const b of [...newBases, ...savedBases]) {
      const ref = referenceOf(b.product);
      const mine = ref ? overrides.filter((o) => !placed.has(o.product.id) && referenceOf(o.product) === ref).sort((x, y) => x.product.platform.localeCompare(y.product.platform)) : [];
      out.push({ ...b, override: false, orphan: false, overrides: mine.length });
      for (const o of mine) {
        placed.add(o.product.id);
        out.push({ ...o, override: true, orphan: false, overrides: 0 });
      }
    }
    for (const o of overrides) if (!placed.has(o.product.id)) out.push({ ...o, override: true, orphan: true, overrides: 0 });
    return out;
  }, [catalog.productList, workspace, search, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = useCallback(
    (row: Row, field: ProductField, value: unknown) => {
      const server = serverById.get(row.product.id);
      const meta = { label: row.product.itemId || row.product.name || 'new product', template: 'Products' };
      if (!workspace.getDraft(entityKey('product', row.product.id)) && !server) return;
      workspace.edit('product', row.product.id, field, value, { base: server ? { version: Number(server.version), value: server as unknown as Json } : undefined, ...meta });
    },
    [workspace, serverById],
  );

  const create = () => {
    const id = workspace.create(
      'product',
      { id: '', itemId: '', name: '', description: null, price: { amount: 0, currency: 'USD' }, platform: 'default', type: 1, baseId: null, iconUrl: null, localizedName: null, localizedDescription: null } as unknown as Json,
      { label: 'new product', template: 'Products' },
    );
    setFocusSku(id);
  };

  const remove = (row: Row) => {
    const p = row.product;
    if (row.draft?.status === 'new') {
      workspace.discardEntity(entityKey('product', p.id)); // a discarded new row leaves nothing behind (Q-D-11)
      if (whereUsed.value === p.id) whereUsed.close();
      return;
    }
    const server = serverById.get(p.id) ?? p;
    workspace.remove('product', p.id, { version: Number(server.version), value: server as unknown as Json }, { label: p.itemId || p.name, template: 'Products' });
  };

  const addOverride = (base: Product, platform: string) => {
    workspace.create(
      'product',
      {
        id: '',
        itemId: base.itemId,
        name: base.name,
        description: base.description ?? null,
        price: { amount: Number(base.price.amount), currency: base.price.currency },
        platform,
        type: Number(base.type),
        // the override keeps the base row's reference: its baseId, else its SKU (the validator matches `baseId ?? itemId` of the default rows)
        baseId: referenceOf(base),
        iconUrl: base.iconUrl ?? null,
        localizedName: base.localizedName ?? null,
        localizedDescription: base.localizedDescription ?? null,
      } as unknown as Json,
      { label: `${base.itemId} · ${platform}`, template: 'Products' },
    );
    setOverrideFor(null);
  };

  const editable = (row: Row) => canWrite && row.draft?.status !== 'deleted';
  const iconAssetId = (url: string | null) => (url ? (catalog.assetList.find((a) => a.contentPath === url)?.id ?? null) : null);
  const stringKey = (id: string | null) => (id ? (catalog.strings.get(id)?.key ?? (workspace.getDraft(entityKey('string', id))?.current as { key?: string } | undefined)?.key ?? id.slice(0, 8)) : '—');

  const textColumn = (key: ProductField, header: string, opts: { nullable?: boolean; width?: number; pattern?: RegExp; patternHint?: string } = {}): Column<Row> => ({
    key,
    header,
    width: opts.width,
    sortValue: (r) => (r.product[key as keyof Product] as string | null) ?? '',
    cellText: (r) => (r.product[key as keyof Product] as string | null) ?? '',
    render: (r) => {
      const v = (r.product[key as keyof Product] as string | null) ?? '';
      return v === '' ? <span className="muted">—</span> : <span title={v}>{v}</span>;
    },
    editor: (r, done) =>
      editable(r) ? (
        <CellText
          label={header}
          value={(r.product[key as keyof Product] as string | null) ?? ''}
          pattern={opts.pattern}
          patternHint={opts.patternHint}
          onCommit={(v) => edit(r, key, opts.nullable && v.trim() === '' ? null : v)}
          onDone={() => done.commit(null)}
        />
      ) : undefined,
  });

  const columns: Column<Row>[] = [
    {
      key: 'platform',
      header: 'Platform',
      fixed: true,
      width: 220,
      sortValue: (r) => r.product.platform,
      cellText: (r) => r.product.platform,
      render: (r) => {
        const d = r.draft;
        const problem = d?.conflict?.message ?? d?.error?.message ?? null;
        const details = (d?.error?.details ?? []).map((x) => x.message).filter(Boolean);
        return (
          <span className={`row product-platform ${r.override ? 'is-override' : ''}`} data-testid={`product-${r.product.id}`}>
            {r.override ? <CornerDownRight className="icon-s product-branch" aria-hidden="true" /> : <Layers className="icon-s product-group-icon" aria-hidden="true" />}
            <code className="small">{r.product.platform}</code>
            {!r.override && r.overrides > 0 && (
              <span className="chip product-override-count" title={`Platform overrides under reference id ${referenceOf(r.product)}`}>
                {r.overrides} override{r.overrides === 1 ? '' : 's'}
              </span>
            )}
            {d && <Badge tone={draftTone[d.status]}>{d.status === 'new' ? 'NEW' : d.status === 'deleted' ? 'DEL' : 'MOD'}</Badge>}
            {r.orphan && (
              <Badge tone="warning" title="No default row on this branch declares this override's base id; the validator refuses it (missing_reference).">
                no base row
              </Badge>
            )}
            {problem && (
              <span className="product-error" role="alert" title={[problem, ...details].join('\n')} data-testid={`product-error-${r.product.id}`}>
                {details[0] ?? problem}
              </span>
            )}
          </span>
        );
      },
      editor: (r, done) =>
        editable(r) && r.override ? (
          <CellText label="Platform" value={r.product.platform} pattern={PLATFORM_SLUG} patternHint="^[a-z0-9][a-z0-9_-]{0,31}$" onCommit={(v) => edit(r, 'platform', v)} onDone={() => done.commit(null)} />
        ) : undefined,
    },
    {
      key: 'icon',
      header: 'Icon',
      width: 70,
      render: (r) => (r.product.iconUrl ? <ProductIcon url={r.product.iconUrl} assetId={iconAssetId(r.product.iconUrl)} itemId={r.product.itemId} /> : <span className="muted">—</span>),
      cellText: (r) => r.product.iconUrl ?? '',
      editor: (r, done) =>
        editable(r) ? (
          <FieldWidget
            param={ICON_PARAM}
            value={iconAssetId(r.product.iconUrl)}
            compact
            autoFocus
            onCommit={(v) => {
              const url = typeof v === 'string' ? (catalog.assets.get(v)?.contentPath ?? null) : null;
              edit(r, 'iconUrl', url);
              done.commit(url);
            }}
            onCancel={done.cancel}
          />
        ) : undefined,
    },
    textColumn('name', 'Name', { width: 180 }),
    {
      ...textColumn('itemId', 'Product id', { width: 200 }),
      render: (r) => {
        if (focusSku === r.product.id && editable(r)) {
          return <CellText label="Product id" value={r.product.itemId} onCommit={(v) => edit(r, 'itemId', v)} onDone={() => setFocusSku(null)} />;
        }
        return r.product.itemId === '' ? <span className="muted product-required">required</span> : <code className="small" title={r.product.itemId}>{r.product.itemId}</code>;
      },
    },
    {
      key: 'price',
      header: 'Price',
      width: 190,
      align: 'right',
      sortValue: (r) => Number(r.product.price.amount),
      cellText: (r) => `${formatMinor(r.product.price.amount, r.product.price.currency)} ${r.product.price.currency}`,
      render: (r) => (
        <span className="row product-price">
          <span>{formatMinor(r.product.price.amount, r.product.price.currency)}</span>
          <code className="small">{r.product.price.currency}</code>
          {!KNOWN_USD_RATES.has(r.product.price.currency) && (
            <Badge tone="warning" title={NO_FX_TITLE}>
              <AlertTriangle aria-hidden="true" />
              no USD rate
            </Badge>
          )}
        </span>
      ),
      editor: (r, done) => (editable(r) ? <PriceEditor amount={Number(r.product.price.amount)} currency={r.product.price.currency} onCommit={(price) => edit(r, 'price', price)} onDone={() => done.commit(null)} /> : undefined),
    },
    {
      key: 'type',
      header: 'Type',
      width: 140,
      sortValue: (r) => Number(r.product.type),
      cellText: (r) => typeLabel(r.product.type),
      render: (r) => typeLabel(r.product.type),
      editor: (r, done) =>
        editable(r) ? (
          <Select
            size="s"
            autoFocus
            aria-label="Type"
            value={String(Number(r.product.type))}
            onChange={(e) => {
              edit(r, 'type', Number(e.target.value));
              done.commit(Number(e.target.value));
            }}
            onBlur={done.cancel}
            onKeyDown={(e) => e.key === 'Escape' && done.cancel()}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={String(t.value)}>
                {t.label}
              </option>
            ))}
          </Select>
        ) : undefined,
    },
    textColumn('baseId', 'Base id', { nullable: true, width: 180 }),
    textColumn('description', 'Description', { nullable: true, width: 220 }),
    {
      key: 'localizedName',
      header: 'Localized name',
      width: 170,
      cellText: (r) => stringKey(r.product.localizedName),
      render: (r) => (r.product.localizedName ? <code className="small" title={catalog.stringText(r.product.localizedName) ?? ''}>{stringKey(r.product.localizedName)}</code> : <span className="muted">—</span>),
      editor: (r, done) =>
        editable(r) ? (
          <FieldWidget
            param={LOC_NAME_PARAM}
            value={r.product.localizedName}
            compact
            autoFocus
            onCommit={(v) => {
              edit(r, 'localizedName', typeof v === 'string' ? v : null);
              done.commit(v);
            }}
            onCancel={done.cancel}
          />
        ) : undefined,
    },
    {
      key: 'localizedDescription',
      header: 'Localized description',
      width: 170,
      cellText: (r) => stringKey(r.product.localizedDescription),
      render: (r) => (r.product.localizedDescription ? <code className="small" title={catalog.stringText(r.product.localizedDescription) ?? ''}>{stringKey(r.product.localizedDescription)}</code> : <span className="muted">—</span>),
      editor: (r, done) =>
        editable(r) ? (
          <FieldWidget
            param={LOC_DESC_PARAM}
            value={r.product.localizedDescription}
            compact
            autoFocus
            onCommit={(v) => {
              edit(r, 'localizedDescription', typeof v === 'string' ? v : null);
              done.commit(v);
            }}
            onCancel={done.cancel}
          />
        ) : undefined,
    },
    {
      key: 'id',
      header: 'Id',
      width: 150,
      cellText: (r) => r.product.legacyId ?? r.product.id,
      render: (r) => (
        <span className="row small">
          <code title={r.product.id}>{isTempId(r.product.id) ? r.product.id : r.product.id.slice(0, 8)}</code>
          {r.product.legacyId && <span className="muted" title="Legacy id">{r.product.legacyId}</span>}
        </span>
      ),
    },
    {
      key: 'menu',
      header: '',
      width: 40,
      fixed: true,
      render: (r) => (
        <RowMenu
          row={r}
          canWrite={canWrite}
          onOverride={() => setOverrideFor(r.product)}
          onWhereUsed={() => whereUsed.open(r.product.id)}
          onDelete={() => remove(r)}
          onRestore={() => workspace.restore('product', r.product.id)}
        />
      ),
    },
  ];

  const total = rows.length;
  return (
    <section className="products-page">
      <div className="page-head">
        <h1>Products</h1>
        {!catalog.loading && (
          <span className="page-meta">
            {catalog.productList.length} product row{catalog.productList.length === 1 ? '' : 's'}
          </span>
        )}
        <span className="spacer" />
        <Button variant="primary" onClick={create} aria-label="New product" missingPermission={missing} disabled={workspace.isProtected} title={workspace.isProtected ? 'Protected branch: changes land through merge requests.' : undefined} icon={<Plus aria-hidden="true" />}>
          Create
        </Button>
      </div>
      <div className="page-toolbar">
        <span className="muted small">Grouped by reference id: the default row, then its platform overrides. Edits are staged; Save All commits them.</span>
      </div>
      {catalog.error && (
        <div className="product-alert" role="alert">
          <AlertCircle className="product-alert-icon" aria-hidden="true" />
          <span>{catalog.error}</span>
        </div>
      )}
      <div data-testid="products-grid">
        <DataGrid
          id="products"
          previews
          rows={rows}
          rowKey={(r) => r.product.id}
          loading={catalog.loading && catalog.productList.length === 0}
          revision={version + catalog.version}
          caption={`${total} row${total === 1 ? '' : 's'}`}
          search={{ value: search, onSearch: setSearch, placeholder: 'Search SKU, base id, name, legacy id…' }}
          revealKey={focusSku}
          rowClass={(r) => [r.override ? 'product-override' : 'product-base', r.draft ? (r.draft.status === 'deleted' ? 'deleted' : 'dirty') : '', r.draft?.error || r.draft?.conflict ? 'product-failed' : ''].filter(Boolean).join(' ')}
          emptyState={
            search ? (
              <EmptyState icon={<Search />} title="No product matches" body={`Nothing on this branch matches “${search}” in the SKU, the base id, the name or the legacy id.`} />
            ) : (
              <EmptyState
                icon={<ShoppingBag />}
                title="No products on this branch yet"
                body="Create adds a default row (Consumable, 0 USD); a row's actions menu adds its platform overrides."
                action={
                  canWrite ? (
                    <Button size="s" onClick={create} icon={<Plus aria-hidden="true" />}>
                      Create a product
                    </Button>
                  ) : undefined
                }
              />
            )
          }
          columns={columns}
        />
      </div>
      {overrideFor && <OverrideDialog base={overrideFor} existing={rows.filter((r) => r.override && referenceOf(r.product) === referenceOf(overrideFor)).map((r) => r.product.platform)} onCancel={() => setOverrideFor(null)} onCreate={(slug) => addOverride(overrideFor, slug)} />}
      {whereUsed.value && <WhereUsedDrawer productId={whereUsed.value} product={rows.find((r) => r.product.id === whereUsed.value)?.product ?? serverById.get(whereUsed.value)} org={org} game={game.slug} gameId={game.id} branch={branch} head={workspace.head} onClose={whereUsed.close} />}
    </section>
  );
}

/** A single-line cell editor: commits on Enter or blur (one change), Escape leaves it unchanged. */
function CellText({ value, onCommit, onDone, label, pattern, patternHint }: { value: string; onCommit: (v: string) => void; onDone: () => void; label: string; pattern?: RegExp; patternHint?: string }) {
  const [text, setText] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const finish = () => {
    if (text !== value) {
      if (pattern && !pattern.test(text)) {
        setError(`Must match ${patternHint ?? pattern.source}`);
        return;
      }
      onCommit(text);
    }
    onDone();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finish();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onDone();
    }
  };
  return <TextInput size="s" autoFocus aria-label={label} value={text} aria-invalid={error ? true : undefined} title={error ?? undefined} onChange={(e) => { setText(e.target.value); setError(null); }} onBlur={finish} onKeyDown={onKeyDown} />;
}

/** The price cell: the amount as a decimal of the currency's minor units (`12.99`) and the currency code beside it. */
function PriceEditor({ amount, currency, onCommit, onDone }: { amount: number; currency: string; onCommit: (price: { amount: number; currency: string }) => void; onDone: () => void }) {
  const [text, setText] = useState(formatMinor(amount, currency));
  const [code, setCode] = useState(currency);
  const [error, setError] = useState<string | null>(null);
  const finish = () => {
    const c = code.trim().toUpperCase();
    if (!CURRENCY_PATTERN.test(c)) {
      setError('A currency code of the registry (USD, NG, EUR…)');
      return;
    }
    const minor = parseMinor(text, c);
    if (minor === null) {
      const exp = exponentOf(c);
      setError(exp === 0 ? `${c} has no minor digits: a whole non-negative number` : `A non-negative amount with at most ${exp} decimals`);
      return;
    }
    if (minor !== amount || c !== currency) onCommit({ amount: minor, currency: c });
    onDone();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finish();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onDone();
    }
  };
  return (
    <span
      className="row product-price-editor"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) finish();
      }}
    >
      <TextInput size="s" autoFocus inputMode="decimal" aria-label="Price" value={text} aria-invalid={error ? true : undefined} title={error ?? undefined} onChange={(e) => { setText(e.target.value); setError(null); }} onKeyDown={onKeyDown} />
      <TextInput size="s" aria-label="Currency" className="product-currency" value={code} maxLength={8} onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }} onKeyDown={onKeyDown} />
    </span>
  );
}

function RowMenu({ row, canWrite, onOverride, onWhereUsed, onDelete, onRestore }: { row: Row; canWrite: boolean; onOverride: () => void; onWhereUsed: () => void; onDelete: () => void; onRestore: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const p = row.product;
  const deleted = row.draft?.status === 'deleted';
  const noSku = !p.itemId;
  return (
    <>
      <IconButton
        label={`Actions for ${p.itemId || p.id}`}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertical aria-hidden="true" />
      </IconButton>
      <Menu
        anchor={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        label="Product actions"
        items={[
          ...(!row.override ? [{ key: 'override', label: noSku ? 'Platform override… (give the row a product id first)' : 'Platform override…', icon: <Plus aria-hidden="true" />, onSelect: onOverride, disabled: !canWrite || deleted || noSku }] : []),
          { key: 'where', label: 'Where used', onSelect: onWhereUsed },
          { key: 's1', label: '', separator: true },
          deleted ? { key: 'restore', label: 'Restore', onSelect: onRestore, disabled: !canWrite } : { key: 'delete', label: 'Delete', onSelect: onDelete, danger: true, disabled: !canWrite },
        ]}
      />
    </>
  );
}

const OTHER = '__other__';

/** + Platform override…: a Nutaku slot of section 9.11 or any platform slug. */
function OverrideDialog({ base, existing, onCancel, onCreate }: { base: Product; existing: string[]; onCancel: () => void; onCreate: (slug: string) => void }) {
  const firstFree = NUTAKU_SLOTS.find((s) => !existing.includes(s.slug))?.slug ?? OTHER;
  const [choice, setChoice] = useState<string>(firstFree);
  const [custom, setCustom] = useState('');
  const slug = choice === OTHER ? custom.trim() : choice;
  const error = slug === '' ? null : !PLATFORM_SLUG.test(slug) ? 'A platform slug matches ^[a-z0-9][a-z0-9_-]{0,31}$.' : slug === 'default' || slug === 'all' ? 'default and all are the base row itself.' : existing.includes(slug) ? `This product already has a ${slug} row.` : null;
  const ready = slug !== '' && error === null;
  return (
    <Modal
      title={`Platform override of ${base.itemId}`}
      open
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="primary" disabled={!ready} onClick={() => onCreate(slug)}>
            Add override
          </Button>
        </>
      }
    >
      <div className="product-dialog">
      <p className="product-dialog-lede">
        A new row with the base id <code>{referenceOf(base)}</code> on the chosen platform; it starts as a copy of the default row and is staged until Save All.
      </p>
      <div className="form-grid">
      <Field className="span-2" label="Platform">
        <Select value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Platform">
          {NUTAKU_SLOTS.map((s) => (
            <option key={s.slug} value={s.slug} disabled={existing.includes(s.slug)}>
              {s.slug} ({s.label}, code {s.code})
            </option>
          ))}
          <option value={OTHER}>Another platform slug…</option>
        </Select>
      </Field>
      {choice === OTHER && (
        <Field className="span-2" label="Platform slug" error={error} help="Lower-case letters, digits, _ and -; up to 32 characters (legacy-11 is the importer's name for Nutaku Android).">
          <TextInput value={custom} onChange={(e) => setCustom(e.target.value)} aria-label="Platform slug" placeholder="legacy-11" autoFocus />
        </Field>
      )}
      </div>
      {choice !== OTHER && error && (
        <div className="product-alert" role="alert">
          <AlertCircle className="product-alert-icon" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      </div>
    </Modal>
  );
}

/** Where used (`?product=<id>`): the document prices that name the product (`GET …/products/{product}/references`). */
function WhereUsedDrawer({ productId, product, org, game, gameId, branch, head, onClose }: { productId: string; product: Product | undefined; org: string; game: string; gameId: string; branch: string; head: string | null; onClose: () => void }) {
  const { client } = useAuth();
  const temp = isTempId(productId);
  const refsQ = useQuery(temp ? null : `branch:${gameId}:${branch}:product-references:${productId}`, () => client.data(ops.listProductReferences, { path: { org, game, branch, product: productId } }), { head });
  const items = ((refsQ.data as { items?: { documentId: string; template: string; path: string }[] } | undefined)?.items ?? []).slice().sort((a, b) => a.template.localeCompare(b.template) || a.documentId.localeCompare(b.documentId) || a.path.localeCompare(b.path));
  const error = refsQ.error instanceof ApiError ? refsQ.error : refsQ.error ? new ApiError(0, 'unavailable', String(refsQ.error), '') : null;
  return (
    <Modal title={`Where used: ${product?.itemId || productId}`} open drawer onClose={onClose}>
      {product && (
        <p className="muted small product-dialog-lede">
          Content refers to products by reference id ({referenceOf(product) || '—'}); the list is every price of the branch that names it.
        </p>
      )}
      {temp ? (
        <EmptyState icon={<ShoppingBag />} title="Not saved yet" body="A new product: nothing references it until it is saved." />
      ) : refsQ.loading && !refsQ.data ? (
        <div className="skeleton-rows" role="status" aria-label="Loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton-row">
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="product-alert" role="alert">
          <AlertCircle className="product-alert-icon" aria-hidden="true" />
          <span>
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </span>
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={<FileText />} title="Not referenced" body="No store item, offer or other price on this branch names this product." />
      ) : (
        <ul className="product-refs" data-testid="product-references">
          {items.map((r) => (
            <li key={`${r.documentId}:${r.path}`} className="product-ref">
              <FileText className="product-ref-icon" aria-hidden="true" />
              <Link to={`${routes.documents(org, game, branch, r.template)}?doc=${encodeURIComponent(r.documentId)}`}>
                {r.template.split('.').pop()} · <code className="small">{r.documentId.slice(0, 8)}</code> <ExternalLink className="icon-s" aria-hidden="true" />
              </Link>{' '}
              <span className="muted small">{r.path}</span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/**
 * The icon preview of a product row: an `iconUrl` naming an asset of the branch shows through a signed URL (M7 slice 3; the
 * public path answers 404 until a pinned release lists the asset); any other URL shows as it is. `iconUrl` itself keeps
 * storing the public path.
 */
function ProductIcon({ url, assetId, itemId }: { url: string; assetId: string | null; itemId: string }) {
  const signed = useAssetUrls(useBranchAssetCtx(), assetId ? [assetId] : [])[assetId ?? ''];
  const src = assetId ? signed : url;
  if (!src) return <span className="thumb placeholder product-thumb" title={url} aria-label={`Icon of ${itemId}`} />;
  return <img className="thumb product-thumb" src={src} alt={`Icon of ${itemId}`} title={url} />;
}
